import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';

interface ArticleLayoutProps {
  title: string;
  subtitle: string;
  readTime: string;
  tag: string;
  children: ReactNode;
}

function generateKeywords(title: string): string {
  const base = 'acbuy spreadsheet, acbuyspreadsheet, product discovery, smart shopping';
  const lower = title.toLowerCase();
  if (lower.includes('explained')) return `${base}, acbuy spreadsheet explained, shopping system`;
  if (lower.includes('deal')) return `${base}, find better deals, price comparison, deal hunting`;
  if (lower.includes('review')) return `${base}, acbuy spreadsheet review, shopping tool review`;
  if (lower.includes('amazon')) return `${base}, acbuy spreadsheet vs amazon, product comparison tool`;
  if (lower.includes('trend')) return `${base}, shopping trends 2026, product intelligence tool`;
  if (lower.includes('beginner')) return `${base}, acbuy spreadsheet beginners, step by step guide`;
  if (lower.includes('safe')) return `${base}, is acbuy spreadsheet safe, shopping safety`;
  if (lower.includes('best product')) return `${base}, best products acbuy, product recommendations`;
  if (lower.includes('save money')) return `${base}, save money shopping, smart spending tips`;
  return base;
}

export default function ArticleLayout({ title, subtitle, readTime, tag, children }: ArticleLayoutProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://acbuy.my';
  const path = '/' + title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const fullUrl = siteUrl.replace(/\/+$/, '') + path;

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    'headline': title,
    'description': subtitle,
    'datePublished': '2026-06-24T08:00:00+00:00',
    'dateModified': '2026-06-24T08:00:00+00:00',
    'author': {
      '@type': 'Organization',
      'name': 'ACBuy Editorial Team',
      'url': siteUrl,
    },
    'publisher': {
      '@type': 'Organization',
      'name': 'ACBuy Spreadsheet',
      'url': siteUrl,
    },
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': fullUrl,
    },
    'inLanguage': 'en-US',
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SEOHead
        title={title}
        description={subtitle}
        keywords={generateKeywords(title)}
        canonicalPath={path}
        ogType="article"
        publishedTime="2026-06-24T08:00:00+00:00"
        modifiedTime="2026-06-24T08:00:00+00:00"
        author="ACBuy Editorial Team"
      >
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
      </SEOHead>

      <Navbar />

      <main>
        {/* Article Header */}
        <section className="relative w-full pt-32 pb-16 md:pt-40 md:pb-20 bg-background-100">
          <div className="w-full max-w-4xl mx-auto px-6 md:px-10">
            <div className="mb-8">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200 whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                Back to ACBuy Spreadsheet Home
              </Link>
            </div>

            <span className="inline-block text-xs font-semibold tracking-widest text-accent-600 uppercase mb-4 bg-accent-50 px-3 py-1.5 rounded-full">
              {tag}
            </span>

            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-900 leading-tight mb-6">
              {title}
            </h1>

            <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-3xl">
              {subtitle}
            </p>

            <div className="flex items-center gap-4 mt-8 text-xs text-foreground-500">
              <span>Published: June 2026</span>
              <span>&bull;</span>
              <span>{readTime}</span>
              <span>&bull;</span>
              <span>By ACBuy Editorial Team</span>
            </div>
          </div>
        </section>

        {/* Article Body */}
        <article className="relative w-full py-16 md:py-20">
          <div className="w-full max-w-4xl mx-auto px-6 md:px-10">
            <div className="prose-custom space-y-8">
              {children}
            </div>

            {/* CTA Block */}
            <div className="mt-16 p-8 md:p-12 bg-foreground-900 rounded-2xl text-center">
              <h3 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
                Ready to Shop Smarter?
              </h3>
              <p className="text-sm text-foreground-300 max-w-lg mx-auto mb-8 leading-relaxed">
                Access the complete <strong className="text-background-50">acbuy spreadsheet</strong> system and
                start discovering better products in less time. No sign-up required.
              </p>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 text-base font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all duration-300 hover:scale-105"
              >
                Access ACBuy Spreadsheet Now
                <i className="ri-arrow-right-up-line text-lg"></i>
              </a>
            </div>

            {/* Back to Home */}
            <div className="mt-12 text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap"
              >
                <i className="ri-home-4-line"></i>
                Return to ACBuy Spreadsheet Homepage
              </Link>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}